import type {
  TeacherCohortProgressMatrixAssignment,
  TeacherCohortProgressMatrixCell,
  TeacherCohortProgressMatrixStudent,
} from "../../auth/api-client";
import { assignmentColumnHeader } from "./cohortProgressCounts";
import { formatMatrixCellExportValue, formatQuizScoreLabel, matrixCellState } from "./formatMatrixCell";
import { ASSIGNMENT_PROGRESS_LABELS } from "../../student/assignmentProgressStatus";
import { studentListLabel } from "../../people/studentRosterLabel";

function csvEscape(value: string): string {
  if (/[",\n\r]/.test(value)) return `"${value.replaceAll('"', '""')}"`;
  return value;
}

function csvRow(values: string[]): string {
  return values.map(csvEscape).join(",");
}

function downloadCsv(filename: string, rows: string[]): void {
  const body = `\uFEFF${rows.join("\r\n")}\r\n`;
  const blob = new Blob([body], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
}

export function exportProgressMatrixGridCsv(args: {
  cohortId: string;
  assignments: readonly TeacherCohortProgressMatrixAssignment[];
  students: readonly TeacherCohortProgressMatrixStudent[];
  cells: readonly TeacherCohortProgressMatrixCell[];
}): void {
  const cellMap = new Map<string, TeacherCohortProgressMatrixCell>();
  for (const c of args.cells) {
    cellMap.set(`${c.studentUserId}:${c.assignmentId}`, c);
  }

  const header = [
    "Student",
    "Email",
    ...args.assignments.map((a) => assignmentColumnHeader(a.lessonTitle, a.itemKind)),
  ];
  const rows: string[] = [csvRow(header)];

  for (const student of args.students) {
    const row = [
      studentListLabel(student),
      student.email,
      ...args.assignments.map((a) => {
        const cell = cellMap.get(`${student.studentUserId}:${a.assignmentId}`);
        return formatMatrixCellExportValue(a, cell);
      }),
    ];
    rows.push(csvRow(row));
  }

  downloadCsv(`cohort-${args.cohortId.slice(0, 8)}-progress-grid.csv`, rows);
}

/** Tidy rows: one line per student-assignment pairing. Ideal for LLM analysis and Sheets pivot tables. */
export function exportProgressMatrixRowsCsv(args: {
  cohortId: string;
  assignments: readonly TeacherCohortProgressMatrixAssignment[];
  students: readonly TeacherCohortProgressMatrixStudent[];
  cells: readonly TeacherCohortProgressMatrixCell[];
}): void {
  const cellMap = new Map<string, TeacherCohortProgressMatrixCell>();
  for (const c of args.cells) {
    cellMap.set(`${c.studentUserId}:${c.assignmentId}`, c);
  }

  const header = [
    "student_name",
    "student_email",
    "assignment_title",
    "assignment_type",
    "assignment_released",
    "assignment_closed",
    "progress_status",
    "quiz_score",
    "quiz_attempts",
    "lesson_last_status",
  ];
  const rows: string[] = [csvRow(header)];

  for (const student of args.students) {
    for (const assignment of args.assignments) {
      const cell = cellMap.get(`${student.studentUserId}:${assignment.assignmentId}`);
      const state = matrixCellState(cell);
      const isQuiz = assignment.itemKind === "quiz";
      const kind =
        assignment.itemKind === "quiz"
          ? "quiz"
          : assignment.itemKind === "presentation"
            ? "presentation"
            : "lesson";
      rows.push(
        csvRow([
          studentListLabel(student),
          student.email,
          assignment.lessonTitle,
          kind,
          assignment.released === false ? "planned" : "assigned",
          assignment.closed ? "yes" : "no",
          ASSIGNMENT_PROGRESS_LABELS[state],
          isQuiz && cell?.quizScoreCorrect != null && cell.quizScoreTotal != null && (cell.quizAttemptCount ?? 0) > 0
            ? formatQuizScoreLabel(cell.quizScoreCorrect, cell.quizScoreTotal, cell.quizAttemptCount ?? 0)
            : "",
          isQuiz && cell?.quizAttemptCount ? String(cell.quizAttemptCount) : "",
          !isQuiz ? (cell?.lastOverallStatus ?? "") : "",
        ])
      );
    }
  }

  downloadCsv(`cohort-${args.cohortId.slice(0, 8)}-progress-rows.csv`, rows);
}
